import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { createDialogContext, DialogProvider } from "../../../../Common/Dialog";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  ModalHeader,
  ModalFooter,
  HStack,
  Button,
  Text,
  Icon,
  Spinner,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FiArrowLeft } from "react-icons/fi";
import { FormProvider, useForm } from "react-hook-form";
import actions from "src/actions";
import { AlgoliaIndexType } from "common/interfaces/Algolia";
import { algoliasearch, SearchClient } from "algoliasearch";
import CreateNewSegmentReview from "./CreateNewSegmentReview";
import CreateNewSegmentComposer from "./CreateNewSegmentComposer";
import { Option } from "src/ui/KYDMultiSelect";
import { KYDGender } from "common/interfaces/KYDUser";
import {
  KYDFilters,
  KYDNumericOperator,
  KYDSegment,
} from "@common/interfaces/KYDCampaign";
import { InstantSearch } from "react-instantsearch";
import { KYDSegmentWithVenues } from "../SegmentDetail";
import { KYDGenreLabel } from "src/Manager/Genres";
import { debounce } from "lodash";
import { useSegment } from "../hooks/useSegments";

export type CreateSegmentFormValues = {
  name: string;
  type: "dynamic" | "static";
  filters: {
    segments: Option[];
    genres: Option[];
    events: Option[];
    artists: Option[];
    venues: Option[];
    event_keywords: Option[];
    gender: KYDGender | "any";
    custom_tags: Option[];
    age: { operator: KYDNumericOperator; value: number };
    average_items_per_order: { operator: KYDNumericOperator; value: number };
    average_order_value: { operator: KYDNumericOperator; value: number };
    order_count: { operator: KYDNumericOperator; value: number };
    total_attendance: { operator: KYDNumericOperator; value: number };
  };
};

export const CreateNewSegmentDialogContext = createDialogContext();

const CreateNewSegmentDialog = ({
  isOpen,
  close,
  onCreate = () => {},
  segment,
  refetch = () => {},
}: {
  onCreate?: (segment: KYDSegment) => void;
  isOpen: boolean;
  close: () => void;
  segment?: KYDSegmentWithVenues | KYDSegment;
  refetch?: () => void;
}) => {
  const toast = useToast();
  const form = useForm<CreateSegmentFormValues>({
    defaultValues: segment
      ? {
          name: segment.name,
          type: segment.type === "dynamic" ? "dynamic" : "static",
          filters: {
            ...segment.filters,
            genres: segment.filters.genres.map((g) => ({
              label: KYDGenreLabel[g] || g,
              value: g,
            })),
            events: segment.filters.events.map((e) => ({ label: e, value: e })),
            artists: segment.filters.performers.map((a) => ({
              label: a,
              value: a,
            })),
            venues: segment.filters.venues.map((v) => ({
              label: v,
              value: v,
            })),
            custom_tags: segment.filters.custom_tags.map((t) => ({
              label: t,
              value: t,
            })),
            age: {
              operator: segment.filters.age.operator,
              value: segment.filters.age.value,
            },
            average_items_per_order: {
              operator: segment.filters.average_items_per_order.operator,
              value: segment.filters.average_items_per_order.value,
            },
            average_order_value: {
              operator: segment.filters.average_order_value.operator,
              value: segment.filters.average_order_value.value,
            },
          },
        }
      : {
          name: "",
          type: "static",
          filters: {
            segments: [],
            gender: "any",
            custom_tags: [],
            average_items_per_order: { operator: ">=", value: 0 },
            average_order_value: { operator: ">=", value: 0 },
            order_count: { operator: ">=", value: 0 },
            total_attendance: { operator: ">=", value: 0 },
            age: { operator: ">=", value: 0 },
            genres: [],
            events: [],
            event_keywords: [],
            artists: [],
            venues: [],
          },
        },
  });
  const {
    state: { segmentSize, segmentSizeLoading },
    actions: { fetchSegmentSize, createSegment, updateSegment },
  } = useSegment();
  const [isLoading, setIsLoading] = useState(false);

  const [eventsAlgoliaConfig, setEventsAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  } | null>(null);

  const [fansAlgoliaConfig, setFansAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  } | null>(null);

  const [step, setStep] = useState(1);

  const fetchEventsAlgoliaConfig = useCallback(async () => {
    const response = await actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.EVENTS,
    });
    if (response.events_config) {
      const search = algoliasearch(
        response.events_config.app_id,
        response.events_config.api_key
      );
      search.clearCache();
      setEventsAlgoliaConfig({
        indexName: response.events_config.index_name,
        searchClient: search,
      });
    }
  }, []);

  const fetchFansAlgoliaConfig = useCallback(async () => {
    const response = await actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.CONTACTS,
    });
    if (response.events_config) {
      const search = algoliasearch(
        response.events_config.app_id,
        response.events_config.api_key
      );
      search.clearCache();
      setFansAlgoliaConfig({
        indexName: response.events_config.index_name,
        searchClient: search,
      });
    }
  }, []);

  const handleCancel = () => {
    if (step === 1) {
      close();
    } else {
      setStep(1);
    }
  };

  const onSubmitFirstStep = useCallback(async () => {
    setIsLoading(true);
    try {
      setStep(2);
    } catch (error) {
      console.error("Error calculating segment size:", error);
      toast({
        title: "Error",
        description: "Unknown error",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [form]);

  const transformFilters = useCallback(
    (filters: CreateSegmentFormValues["filters"]) => {
      return {
        ...filters,
        genres: filters.genres.map((genre) => genre.value),
        events: filters.events.map((event) => event.value),
        performers: filters.artists.map((artist) => artist.value),
        venues: filters.venues.map((venue) => venue.value),
        gender: filters.gender,
        custom_tags: filters.custom_tags.map((tag) => tag.value),
        age: filters.age
          ? {
              operator: filters.age.operator,
              value: filters.age.value || 0,
            }
          : undefined,
        average_items_per_order: filters.average_items_per_order
          ? {
              operator: filters.average_items_per_order.operator,
              value: filters.average_items_per_order.value || 0,
            }
          : undefined,
        average_order_value: filters.average_order_value
          ? {
              operator: filters.average_order_value.operator,
              value: filters.average_order_value.value || 0,
            }
          : undefined,
        order_count: filters.order_count
          ? {
              operator: filters.order_count.operator,
              value: filters.order_count.value || 0,
            }
          : undefined,
      };
    },
    []
  );

  const createSegmentQuery = useCallback(
    async (filters: KYDFilters) => {
      return actions.ADMIN.createSegmentQuery({ filters });
    },
    [transformFilters]
  );

  const onSubmitSecondStep = useCallback(
    async (data: CreateSegmentFormValues) => {
      setIsLoading(true);
      try {
        const filters = transformFilters(data.filters);
        const query = await createSegmentQuery(filters);
        console.log("type", data.type);

        if (!segment) {
          const response = await actions.ADMIN.createSegment({
            name: data.name,
            filters,
            query_execution_id: query.query_execution_id,
            type: data.type,
          });
          onCreate(response);
        } else {
          await actions.ADMIN.updateSegment(segment.id, {
            name: data.name,
            type: data.type,
            filters,
            query_execution_id: query.query_execution_id,
          });
        }

        toast({
          title: "Success",
          description: "Segment created",
          status: "success",
        });
        close();
        refetch();
        form.reset();
        setStep(1);
      } catch (error) {
        console.error("Error calculating segment size:", error);
        toast({
          title: "Error",
          description: "Unknown error",
          status: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [form, segment]
  );

  const handleSave = () => {
    if (step === 1) {
      form.handleSubmit(onSubmitFirstStep)();
    } else {
      form.handleSubmit(onSubmitSecondStep)();
    }
  };

  const debouncedFetchSegmentSize = useMemo(
    () => debounce(fetchSegmentSize, 1000),
    [fetchSegmentSize]
  );

  useEffect(() => {
    fetchEventsAlgoliaConfig();
    fetchFansAlgoliaConfig();
    fetchSegmentSize(transformFilters(form.getValues().filters));
  }, [fetchEventsAlgoliaConfig, fetchFansAlgoliaConfig]);

  useEffect(() => {
    const subscription = form.watch((values) => {
      debouncedFetchSegmentSize(
        transformFilters(values.filters as CreateSegmentFormValues["filters"])
      );
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [form, fetchSegmentSize]);

  return (
    <Modal isOpen={isOpen} onClose={close} size="3xl" isCentered>
      <ModalOverlay />
      <ModalContent bg="white" borderRadius="lg" p={0} minH="40vh">
        {eventsAlgoliaConfig && fansAlgoliaConfig ? (
          <FormProvider {...form}>
            <Box p={6} id="segment-form">
              <ModalHeader p={0} mb={4} fontSize="2xl">
                <HStack justify="space-between">
                  <Text fontWeight="400" fontSize="24px">
                    {step === 1
                      ? segment
                        ? "Edit Segment"
                        : "Create New Segment"
                      : "Review segment details"}
                  </Text>
                  <HStack spacing={3}>
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      rounded="full"
                      fontSize="13px"
                      leftIcon={
                        step === 1 ? undefined : <Icon as={FiArrowLeft} />
                      }
                    >
                      {step === 1 ? "Cancel" : "Back to edit"}
                    </Button>
                    <Button
                      onClick={handleSave}
                      colorScheme="green"
                      isDisabled={!form.watch("name")}
                      rounded="full"
                      fontSize="13px"
                      isLoading={isLoading}
                    >
                      {step === 1 ? "Next" : "Save Segment"}
                    </Button>
                  </HStack>
                </HStack>
              </ModalHeader>

              {step === 1 ? (
                <InstantSearch
                  searchClient={fansAlgoliaConfig?.searchClient}
                  indexName={fansAlgoliaConfig?.indexName}
                >
                  <CreateNewSegmentComposer
                    isUpdate={!!segment}
                    segmentSize={segmentSize}
                    eventsAlgoliaConfig={eventsAlgoliaConfig}
                    segmentSizeLoading={segmentSizeLoading}
                  />
                </InstantSearch>
              ) : (
                <CreateNewSegmentReview
                  eventsAlgoliaConfig={eventsAlgoliaConfig}
                  data={form.getValues()}
                  segmentSize={segmentSize}
                />
              )}
            </Box>
          </FormProvider>
        ) : (
          <VStack
            h="full"
            w="full"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
          >
            <Spinner />
          </VStack>
        )}
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
};

export const withCreateNewSegmentDialog =
  <P extends object>(Component: React.ComponentType<P>) =>
  (props: P & { onChange?: (segment: KYDSegment) => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
      <DialogProvider context={CreateNewSegmentDialogContext}>
        <Component {...props} />
        <CreateNewSegmentDialog
          onCreate={props.onChange || (() => {})}
          isOpen={isOpen}
          close={() => {
            setIsOpen(false);
          }}
        />
      </DialogProvider>
    );
  };

export default CreateNewSegmentDialog;
